import { Link, router, usePage } from "@inertiajs/react"
import {
  Bell,
  BookOpen,
  CalendarCheck,
  Compass,
  Folder,
  Globe,
  Hand,
  History,
  Megaphone,
  Monitor,
  PlayCircle,
  ScrollText,
  Smartphone,
  UserRound,
  Video,
} from "lucide-react"
import { useMemo } from "react"

import { PwaTabBar } from "@/components/member/pwa-tab-bar"
import { NavFooter } from "@/components/nav-footer"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { SidebarSwitchButton } from "@/components/sidebar-switch-button"
import { Button } from "@/components/ui/button"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar"
import { usePwaFormFactor } from "@/hooks/use-pwa-form-factor"
import { useStandaloneDisplay } from "@/hooks/use-standalone-display"
import { useSubscription } from "@/hooks/use-subscription"
import { useUnreadNotifications } from "@/hooks/use-unread-notifications"
import { pwaInstallPath, selectPwaInstallTab } from "@/lib/pwa-device"
import {
  adminPath,
  memberAnnouncementsPath,
  memberEventsCompletedPath,
  memberEventsExplorePath,
  memberEventsUpcomingPath,
  memberInstallPath,
  memberMaterialsPath,
  memberMudraPath,
  memberNotificationsPath,
  memberPath,
  memberReplaysPath,
  memberSubscribePath,
  memberSutrasPath,
  memberTeacherProfilePath,
} from "@/routes"
import type { NavItem, SharedData } from "@/types"

import AppLogo from "./app-logo"

const footerNavItems: NavItem[] = [
  {
    title: "Repository",
    href: "https://github.com/inertia-rails/react-starter-kit",
    icon: Folder,
  },
  {
    title: "Documentation",
    href: "https://inertia-rails.dev",
    icon: BookOpen,
  },
]

export function MemberSidebar() {
  const page = usePage<SharedData>()
  const announcementsCount = page.props.announcements_count ?? 0
  const sidebarCounts = page.props.member_sidebar_counts
  const { isSubscribed } = useSubscription()
  const unreadCount = useUnreadNotifications()
  const { setOpenMobile } = useSidebar()
  const standalone = useStandaloneDisplay()
  const formFactor = usePwaFormFactor()
  const currentPath = page.url.split(/[?#]/, 1)[0]
  const onInstallPage = currentPath === memberInstallPath()
  const hasTeacherProfile = page.props.auth.user?.has_teacher_profile ?? false
  const features = page.props.features
  const showMudra = page.props.auth.user?.admin
    ? true
    : (features?.mudras_enabled_for_subscribers ?? true)
  const showSutras =
    page.props.auth.user?.admin === true || features?.sutras_in_app === true

  const mainNavItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      {
        title: "Live Online",
        href: memberPath(),
        icon: Video,
        isActive: currentPath === memberPath(),
      },
      {
        title: "On-Demand",
        href: memberReplaysPath(),
        icon: PlayCircle,
        badge: sidebarCounts?.on_demand ?? 0,
      },
      {
        title: "Explore",
        href: memberEventsExplorePath(),
        icon: Compass,
        badge: sidebarCounts?.programs ?? 0,
      },
      ...(showMudra
        ? [
            {
              title: "Mudra",
              href: memberMudraPath(),
              icon: Hand,
            },
          ]
        : []),
      ...(showSutras
        ? [
            {
              title: "TriYoga Sutras",
              href: memberSutrasPath(),
              icon: ScrollText,
            },
          ]
        : []),
      {
        title: "Announcements",
        href: memberAnnouncementsPath(),
        icon: Megaphone,
        badge: announcementsCount,
      },
    ]

    return items
  }, [announcementsCount, currentPath, showMudra, showSutras, sidebarCounts])

  const myProgramsNavItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      {
        title: "Upcoming",
        href: memberEventsUpcomingPath(),
        icon: CalendarCheck,
        badge: sidebarCounts?.upcoming ?? 0,
      },
      {
        title: "Completed",
        href: memberEventsCompletedPath(),
        icon: History,
        badge: sidebarCounts?.completed ?? 0,
      },
      {
        title: "Materials",
        href: memberMaterialsPath(),
        icon: BookOpen,
        badge: sidebarCounts?.materials ?? 0,
      },
    ]

    return items
  }, [sidebarCounts])

  return (
    <>
      <Sidebar collapsible="icon" variant="inset">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link href={memberPath()} prefetch>
                  <AppLogo />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <NavMain items={mainNavItems} />
          <NavMain items={myProgramsNavItems} label="My Programs" />

          {hasTeacherProfile && (
            <NavMain
              items={[
                {
                  title: "My Teacher Profile",
                  href: memberTeacherProfilePath(),
                  icon: UserRound,
                  isActive: currentPath === memberTeacherProfilePath(),
                },
              ]}
            />
          )}

          <SidebarGroup className="px-2 py-0">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={page.url.startsWith(memberNotificationsPath())}
                  tooltip={{ children: "Notifications" }}
                >
                  <Link href={memberNotificationsPath()} prefetch>
                    <Bell className="h-4 w-4" />
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <span className="border-border bg-muted/40 text-muted-foreground ml-auto inline-flex min-w-4 items-center justify-center rounded border px-1 py-px text-[10px] leading-none font-medium group-data-[collapsible=icon]:hidden">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {!standalone && formFactor && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={onInstallPage}
                    tooltip={{
                      children: `Install ${formFactor === "mobile" ? "Mobile" : "Desktop"} app`,
                    }}
                  >
                    <Link
                      href={pwaInstallPath(formFactor)}
                      onClick={(event) => {
                        event.preventDefault()
                        if (onInstallPage) {
                          selectPwaInstallTab(formFactor)
                        } else {
                          router.visit(pwaInstallPath(formFactor))
                        }
                        setOpenMobile(false)
                      }}
                    >
                      {formFactor === "mobile" ? <Smartphone /> : <Monitor />}
                      <span>
                        Install {formFactor === "mobile" ? "Mobile" : "Desktop"}{" "}
                        app
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroup>

          {!isSubscribed && (
            <div className="px-3 py-2">
              <Button
                asChild
                className="w-full"
                size="sm"
                style={{ backgroundColor: "#0866ff", color: "white" }}
              >
                <Link href={memberSubscribePath()}>Subscribe</Link>
              </Button>
            </div>
          )}

          <SidebarSeparator className="my-2" />

          <SidebarGroup className="px-2 py-0">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={page.url === "/" || page.url === ""}
                  tooltip={{ children: "Main Site" }}
                >
                  <Link href="/" prefetch>
                    <Globe />
                    <span>Main Site</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            {page.props.auth.user?.admin && (
              <div className="py-1 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
                <SidebarSwitchButton href={adminPath()}>
                  Go to admin
                </SidebarSwitchButton>
              </div>
            )}
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <NavFooter items={footerNavItems} className="mt-auto hidden" />
          <NavUser />
        </SidebarFooter>
      </Sidebar>
      <PwaTabBar />
    </>
  )
}
